import { styled } from 'css-literal-loader/styled';
import PropTypes from 'prop-types';
import React from 'react';
import BaseForm from 'react-formal';

import Button from './Button';
import FieldGroup from './FieldGroup';
import FormField from './FormField';
import FormLabel from './FormLabel';
import FormMessage from './FormMessage';
import Spinner from './Spinner';
import { styles } from './Tooltip';

BaseForm.setDefaults({
  events: ({ valid }) => (valid ? 'onBlur' : ['onChange', 'onBlur']),
});

const TOOLTIP_SELECTOR =
  '[data-validation="tooltip"][data-visible="true"] > *';

class Form extends React.Component {
  static propTypes = {
    innerRef: PropTypes.any,
    submitForm: PropTypes.func,
    onChange: PropTypes.func,
    focusFirstInvalidOnSubmit: PropTypes.bool,
  };

  handleChange = (...args) => {
    const { onChange } = this.props;
    this.formIsDirty = true;
    if (onChange) onChange(...args);
  };

  handleBeforeSubmit = () => {
    this.tooltips = document.querySelectorAll(TOOLTIP_SELECTOR);
  };

  handleInvalidSubmit = errors => {
    const { handleInvalidSubmit, focusFirstInvalidOnSubmit } = this.props;
    for (const tip of this.tooltips) {
      if (tip.classList.contains(styles.shake)) {
        tip.classList.remove(styles.shake);
        tip.offsetHeight; // eslint-disable-line
      }
      tip.classList.add(styles.shake);
      tip.addEventListener(
        'animationend',
        () => tip.classList.remove(styles.shake),
        { once: true },
      );
    }

    if (focusFirstInvalidOnSubmit) {
      const firstInvalid = document.querySelectorAll(
        '[aria-invalid="true"]',
      )[0];
      if (firstInvalid) firstInvalid.focus();
    }

    if (handleInvalidSubmit) handleInvalidSubmit(errors);
  };

  submitForm = async formValue => {
    const { submitForm } = this.props;

    this.formIsDirty = false;

    try {
      await submitForm(formValue);
      this.formIsDirty = false;
    } catch (err) {
      // eslint-disable-next-line
      console.error(err); // sometimes the errors get swallowed...
      throw err;
    }
  };

  render() {
    const {
      innerRef,
      submitForm,
      focusFirstInvalidOnSubmit: _,
      ...props
    } = this.props;

    return (
      <BaseForm
        {...props}
        ref={innerRef}
        onChange={this.handleChange}
        submitForm={submitForm && this.submitForm}
        onBeforeSubmit={this.handleBeforeSubmit}
        onInvalidSubmit={this.handleInvalidSubmit}
      />
    );
  }
}

const InlineSpinner = styled(Spinner, null, "InlineSpinner", require("./Form-InlineSpinner.module.scss"), "inline-spinner", "inlineSpinner");

// eslint-disable-next-line react/no-multi-comp
const FormSubmitButton = React.forwardRef(
  ({ as: Tag = Button, ...outerProps }, ref) => (
    <BaseForm.Submit {...outerProps}>
      {({ submitting, props: { spinner, disableOnSubmit, ...props } }) => (
        <Tag ref={ref} disabled={disableOnSubmit && submitting} {...props}>
          {submitting ? spinner || <InlineSpinner /> : outerProps.children}
        </Tag>
      )}
    </BaseForm.Submit>
  ),
);

export default Object.assign(
  // eslint-disable-next-line react/no-multi-comp
  React.forwardRef((props, ref) => <Form {...props} innerRef={ref} />),
  BaseForm.statics,
  {
    FieldGroup,
    Field: FormField,
    Label: FormLabel,
    SubmitButton: FormSubmitButton,
    Message: FormMessage,
  },
);
